import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { ChatApiService } from './chat-api.service';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private readonly api = inject(ChatApiService);
  private readonly auth = inject(AuthService);

  async registerForAdmin(): Promise<void> {
    const role = this.auth.user()?.role;
    if (role !== 'BusinessAdmin') {
      return;
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return;
    }

    if (!window.isSecureContext && location.hostname !== 'localhost') {
      return;
    }

    try {
      const { publicKey } = await firstValueFrom(this.api.vapidPublicKey());
      const registration = await navigator.serviceWorker.register('/push-sw.js', {
        scope: '/'
      });
      await navigator.serviceWorker.ready;

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return;
      }

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey)
        });
      }

      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.['p256dh'] || !json.keys?.['auth']) {
        return;
      }

      await firstValueFrom(
        this.api.subscribePush({
          endpoint: json.endpoint,
          p256dh: json.keys['p256dh'],
          auth: json.keys['auth']
        })
      );
    } catch {
      // Push is best-effort (HTTP localhost / missing VAPID / denied permission).
    }
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}
